import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Upload, Loader2, X, Paperclip, Camera } from 'lucide-react';
import { toast } from 'sonner';

/**
 * FileUpload component - uploads files to object storage and returns URL list.
 * Feb-17: added dedicated "Take Photo" button that opens the device camera
 * on mobile (via capture="environment") and works on desktop webcams too.
 *
 * Props:
 *   value: string[] - current attachment URLs
 *   onChange: (urls: string[]) => void
 *   maxFiles: number (default 5)
 *   accept: string (default common types)
 *   showCamera: boolean (default true when accept starts with image/*)
 */
export default function FileUpload({ value = [], onChange, maxFiles = 5, accept = 'image/*,application/pdf,video/mp4,.csv,.txt', showCamera }) {
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const shouldShowCamera = showCamera !== undefined ? showCamera : /image\//.test(accept);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (value.length + files.length > maxFiles) {
      toast.error(`Max ${maxFiles} files allowed`);
      return;
    }
    setUploading(true);
    const newUrls = [...value];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        newUrls.push(r.data.url);
      } catch (err) {
        const msg = err.response?.data?.detail || 'Upload failed';
        toast.error(`${f.name}: ${msg}`);
      }
    }
    onChange(newUrls);
    setUploading(false);
    e.target.value = '';
  };

  const remove = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 items-center">
        <label data-testid="file-upload-trigger" className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:border-teal-500 hover:bg-teal-50 cursor-pointer text-sm text-slate-700 transition ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : 'Upload File'}
          <input data-testid="file-upload-input" type="file" multiple accept={accept} onChange={handleFile} className="hidden" />
        </label>
        {shouldShowCamera && (
          <label data-testid="file-camera-trigger" className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-teal-400 bg-teal-50 hover:bg-teal-100 cursor-pointer text-sm text-teal-800 transition ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            <Camera className="w-4 h-4" />
            Take Photo
            <input
              ref={cameraInputRef}
              data-testid="file-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
            />
          </label>
        )}
        <span className="text-xs text-muted-foreground">{value.length} / {maxFiles} files · max 20MB each</span>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((url, i) => {
            const isImg = /\.(png|jpe?g|gif|webp|heic|heif)(\?|$)/i.test(url);
            return (
              <div key={i} className="inline-flex items-center gap-1.5 px-2 py-1 bg-teal-50 border border-teal-200 rounded text-xs text-teal-800">
                {isImg ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1">
                    <img src={url} alt={`upload-${i}`} className="w-8 h-8 rounded object-cover ring-1 ring-teal-200" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="hover:underline">Photo {i + 1}</span>
                  </a>
                ) : (
                  <>
                    <Paperclip className="w-3 h-3" />
                    <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[140px]">{url.split('/').pop()}</a>
                  </>
                )}
                <button data-testid={`remove-file-${i}`} onClick={() => remove(i)} className="hover:bg-teal-100 rounded p-0.5"><X className="w-3 h-3" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
